import clsx from "clsx";
import Head from "next/head";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import { FaTimes } from "react-icons/fa";

import "react-phone-number-input/style.css";

import PhoneInput from "react-phone-number-input";
const Logo = dynamic(
  () => import("@/components/Logo").then((mod) => mod.Logo),
  { ssr: true }
);

import dynamic from "next/dynamic";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
const SuccessContactUs = dynamic(
  () => import("@/components/SuccessContactUs").then((mod) => mod.default),
  { ssr: true }
);
import Image from "next/image";
import Select from "react-select";
import { useRouter } from "next/router";
import { Input, PhoneNumberInput } from "@/components/TextField";
import { NewContainer } from "@/components/NewContainer";
import { SITE_URL } from "@/lib/constants";

const dataIndustry = [
  { label: "Choose Industry...", value: 0 },
  { label: "E-commerce & Retail", value: "E-commerce & Retail" },
  { label: "Professional Services", value: "Professional Services" },
  { label: "Hospitality & F&B", value: "Hospitality & F&B" },
  { label: "Technology & SaaS", value: "Technology & SaaS" },
  { label: "Construction & Trades", value: "Construction & Trades" },
  { label: "Healthcare", value: "Healthcare" },
  { label: "Property & Real Estate", value: "Property & Real Estate" },
  { label: "Other Industry", value: "other" },
];

/* ─── Illustration data for the left panel ─── */
const illustrations = [
  { src: "/contact/illustration-audit.png", alt: "Audit", w: 150, h: 105, className: "top-[8%] left-[2%]", bg: "bg-[#FBDDD8]" },
  { src: "/contact/illustration-growth.png", alt: "Growth", w: 210, h: 188, className: "top-[12%] left-[50%] -translate-x-1/2", bg: "bg-[#D4E4F7]" },
  { src: "/contact/illustration-report.png", alt: "Report", w: 150, h: 90, className: "top-[8%] right-[3%]", bg: "bg-[#FAE3C8]" },
  { src: "/contact/illustration-tax.png", alt: "Tax", w: 170, h: 96, className: "top-[52%] left-[2%]", bg: "bg-[#F5DEB3]" },
  { src: "/contact/illustration-calculator.png", alt: "Calculator", w: 140, h: 88, className: "top-[56%] left-[34%]", bg: "bg-[#D4E4F7]" },
  { src: "/contact/illustration-money.png", alt: "Money", w: 160, h: 122, className: "top-[46%] right-[2%]", bg: "bg-[#FBDDD8]" },
];

const softwareLogos = [
  { name: "Xero", src: "/logos/xero.svg", className: "top-[4%] right-[28%]", color: "bg-[#13B5EA]" },
  { name: "QuickBooks", src: "/logos/quickbooks.svg", className: "top-[36%] left-[16%]", color: "bg-[#2CA01C]" },
  { name: "Odoo", src: "/logos/odoo.svg", className: "top-[70%] left-[28%]", color: "bg-white shadow-md" },
  { name: "SAP", src: "/logos/sap.svg", className: "top-[72%] right-[8%]", color: "bg-[#0070F2]" },
];

const clientLogos = [
  { name: "ShopBack", src: "/logos/clients/client-1.png" },
  { name: "Biles", src: "/logos/clients/client-2.png", bg: "#f6f7fb" },
  { name: "Ruma", src: "/logos/clients/client-3.png", bg: "#2f4054" },
  { name: "GK Horizons", src: "/logos/clients/client-4.png", bg: "#f6f7fb" },
  { name: "WLM", src: "/logos/clients/client-5.png" },
  { name: "Dore", src: "/logos/clients/client-6.png", bg: "#f6f7fb" },
  { name: "Shelly", src: "/logos/clients/client-7.png" },
  { name: "Muvo", src: "/logos/clients/client-8.png", bg: "#f6f7fb" },
  { name: "Umawar", src: "/logos/clients/client-9.png" },
  { name: "Investible", src: "/logos/clients/client-10.png", bg: "#f6f7fb" },
];

/* ─── Decorative dot positions ─── */
const decorativeDots = [
  { size: 12, className: "top-[10%] left-[42%]" },
  { size: 8, className: "top-[6%] right-[12%]" },
  { size: 10, className: "top-[36%] left-[2%]" },
  { size: 8, className: "top-[42%] right-[8%]" },
  { size: 12, className: "top-[76%] left-[12%]" },
  { size: 8, className: "top-[72%] left-[50%]" },
  { size: 10, className: "top-[74%] right-[22%]" },
  { size: 8, className: "top-[30%] right-[2%]" },
];

/* ─── Left Panel Hero Component ─── */
const ContactHeroPanel = () => {
  return (
    <div className="flex flex-col h-full w-full bg-transparent rounded-2xl p-8 lg:p-10 xl:p-12 relative overflow-hidden">
      {/* Heading */}
      <h1 className="font-inter text-[36px] font-bold text-[#004B94] leading-[46px]">
        {"Let's"} work together
      </h1>
      <p className="mt-3 font-inter text-[14px] text-[#6B7280] leading-[20px] max-w-[480px]">
        {"We'd"} love to hear from you! Send us a message using the form
        opposite, or email us.
      </p>

      {/* Illustration area */}
      <div className="relative flex-1 mt-6 min-h-[350px]">
        {/* Floating illustrations */}
        {illustrations.map((img, i) => (
          <div
            key={img.alt}
            className={clsx(
              "absolute rounded-2xl overflow-hidden shadow-sm p-4",
              img.className,
              img.bg
            )}
            style={{
              width: img.w,
              height: img.h,
            }}
          >
            <Image
              src={img.src}
              alt={img.alt}
              width={img.w}
              height={img.h}
              className="w-full h-full object-contain relative z-10"
              unoptimized
            />
          </div>
        ))}

        {/* Software logo circles */}
        {softwareLogos.map((logo) => (
          <div
            key={logo.name}
            className={clsx(
              "absolute w-[42px] h-[42px] rounded-full flex items-center justify-center shadow-sm",
              logo.className,
              logo.color
            )}
          >
            <Image
              src={logo.src}
              alt={logo.name}
              width={24}
              height={24}
              className="w-[24px] h-[24px] object-contain"
              unoptimized
              onError={(e) => {
                // Fallback: show name initial
                e.target.style.display = "none";
              }}
            />
          </div>
        ))}

        {/* Decorative dots */}
        {decorativeDots.map((dot, i) => (
          <div
            key={`dot-${i}`}
            className={clsx(
              "absolute rounded-full bg-blue-primary-200/50",
              dot.className
            )}
            style={{ width: dot.size, height: dot.size }}
          />
        ))}
      </div>

      {/* Client logos section */}
      <div className="mt-auto pt-4">
        <p className="font-inter text-[14px] font-semibold text-gray-cool-900 mb-3">
          Join our 100+ clients
        </p>
        <div className="flex items-center gap-4 mt-3">
          {clientLogos.map((logo) => (
            <div
              key={logo.name}
              className="w-[40px] h-[40px] rounded-full border-[3px] border-[#BDDEFF] overflow-hidden shrink-0"
              style={{ backgroundColor: logo.bg || "white" }}
            >
              <Image
                src={logo.src}
                alt={logo.name}
                width={40}
                height={40}
                className="w-full h-full object-cover rounded-full"
                unoptimized
              />
            </div>
          ))}
          <div className="w-[40px] h-[40px] rounded-full border-[3px] border-[#BDDEFF] bg-[#f6f7fb] flex items-center justify-center shrink-0">
            <span className="font-inter text-[12px] font-medium text-gray-cool-500">+more</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Contact Form Component (shared desktop & mobile) ─── */
const ContactForm = ({
  form,
  onChange,
  phoneChange,
  submit,
  isError,
  messageError,
  isOther,
  selectedIndustry,
  selectIndustry,
  clearFormIndustry,
  loading,
  recaptchaToken,
  setRecaptchaToken,
  recaptchaRef,
}) => {
  return (
    <form onSubmit={submit}>
      <div className="flex w-full h-full flex-col space-y-[20px]">
        <div className="z-10 space-y-[20px] font-inter">
          <div className="block w-full">
            <div className="flex w-full flex-col">
              <Input
                value={form.name}
                onChange={onChange}
                placeholder={"Your Name"}
                name={"name"}
                id={"name"}
                error={isError.name}
                type={"text"}
                messageError={messageError.name}
              />
            </div>
          </div>
          <div className="block w-full">
            <div className="flex w-full flex-col">
              <Input
                value={form.email}
                onChange={onChange}
                placeholder={"Email Address"}
                name={"email"}
                id={"email"}
                error={isError.email}
                type={"email"}
                messageError={messageError.email}
              />
            </div>
          </div>
        </div>
        <PhoneNumberInput
          onChange={phoneChange}
          value={form.phone}
          placeholder={"Phone Number"}
          country={"AU"}
          error={isError.phone}
          messageError={messageError.phone}
        />
        <div
          className={clsx(
            isError.phone ? "-top-8" : "",
            "relative font-inter"
          )}
        >
          <Select
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderColor: isError.industry ? "#F04438" : "#d1d5db",
              }),
            }}
            onChange={selectIndustry}
            value={selectedIndustry}
            className="basic-single h-[45px]"
            classNamePrefix="select"
            defaultValue={dataIndustry[0]}
            isClearable={false}
            isSearchable={true}
            name="industry"
            options={dataIndustry}
          />
        </div>
        {isOther ? (
          <div
            className={clsx(
              isError.phone ? "-top-8" : "",
              "relative font-inter"
            )}
          >
            <input
              type="text"
              name={"industry"}
              id={"industry"}
              className={clsx(
                "h-[45px] px-[10px] w-full border rounded-md",
                isError.industry
                  ? "border-[#F04438] focus:outline-[#F04438] focus:outline-double focus:outline-1 focus:ring-4 focus:ring-[#FECDCA]"
                  : "focus:outline-[#0074E6] focus:outline-double focus:outline-1 focus:ring-4 focus:ring-[#BCDBFB] "
              )}
              onChange={onChange}
              placeholder={"Your Industry"}
              value={form.industry}
            />
            <div
              onClick={clearFormIndustry}
              className="absolute w-14 h-full z-20 flex items-center justify-center bottom-0 right-0"
            >
              <FaTimes width={10} height={10} />
            </div>
          </div>
        ) : (
          ""
        )}

        {isError.industry ? (
          <p
            className={clsx(
              "relative -top-5 font-inter text-sm text-[#F04438]"
            )}
          >
            {messageError.industry}
          </p>
        ) : (
          ""
        )}

        <div
          className={clsx(
            isError.industry ? "-top-8 " : "",
            "relative font-inter"
          )}
        >
          <div className="block w-full">
            <div className="flex w-full flex-col">
              <textarea
                name={"message"}
                id={"message"}
                onChange={onChange}
                placeholder={"How can we help?"}
                className={clsx(
                  "border border-gray-cool-300 rounded-md p-3 h-[100px] w-full text-[14px]",
                  isError.message
                    ? "border-[#F04438] focus:outline-[#F04438] focus:outline-double focus:outline-1 focus:ring-4 focus:ring-[#FECDCA]"
                    : "focus:outline-[#0074E6] focus:outline-double focus:outline-1 focus:ring-4 focus:ring-[#BCDBFB] "
                )}
              ></textarea>
              {isError.message ? (
                <p className="pt-2 font-inter text-sm text-[#F04438]">
                  {messageError.message}
                </p>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
          onChange={(token) => setRecaptchaToken(token)}
          onExpired={() => setRecaptchaToken(null)}
        />
      </div>
      <div className="mt-4 flex justify-end">
        <button
          disabled={loading || !recaptchaToken}
          className={clsx(
            "flex font-inter font-semibold items-center justify-center rounded-md py-2 px-4 text-white text-[14px] hover:bg-[#005bb5] transition-colors min-h-[36px] min-w-[36px]",
            !loading && recaptchaToken
              ? "bg-[#0074E6]"
              : "bg-gray-cool-400 cursor-not-allowed"
          )}
        >
          {loading ? "Sending..." : "Send"}
        </button>
      </div>
    </form>
  );
};

const Contact = () => {
  const router = useRouter();

  const { query } = router;

  const [success, setsuccess] = useState(false);
  const [loading, setloading] = useState(false);
  const [isOther, setisOther] = useState(false);
  const [selectedIndustry, setselectedIndustry] = useState(dataIndustry[0]);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handleBack = () => {
    if (
      typeof window !== "undefined" &&
      document.referrer &&
      (() => {
        try {
          return new URL(document.referrer).origin === window.location.origin;
        } catch {
          return false;
        }
      })()
    ) {
      router.back();
    } else {
      router.push("/");
    }
  };

  const [form, setform] = useState({
    name: null,
    email: null,
    phone: null,
    message: null,
    industry: null,
  });

  const [isError, setisError] = useState({
    name: false,
    email: false,
    phone: false,
    message: false,
    industry: false,
  });

  const [messageError, setmessageError] = useState({
    name: null,
    email: null,
    phone: null,
    message: null,
    industry: null,
  });

  function phoneChange(e) {
    setform({ ...form, ["phone"]: e });
    setisError({ ...isError, ["phone"]: false });
    setmessageError({ ...messageError, ["phone"]: null });
  }

  function onChange(e) {
    setform({ ...form, [e.target.name]: e.target.value });
    setisError({ ...isError, [e.target.name]: false });
    setmessageError({ ...messageError, [e.target.name]: null });
  }

  function submit(e) {
    e.preventDefault();
    if (!recaptchaToken) return;
    setloading(true);
    form["country"] = "AU";
    axios
      .post("/api/email", { ...form, recaptchaToken })
      .then(() => {
        setsuccess(true);
        setloading(false);
      })
      .catch((error) => {
        setloading(false);
        setRecaptchaToken(null);
        if (recaptchaRef.current) recaptchaRef.current.reset();
        if (error.response?.data?.details) {
          error.response.data.details.map((val) => {
            console.log(val);
            setisError({ ...isError, [val.path[0]]: true });
            setmessageError({
              ...messageError,
              [val.path[0]]: val.context.label,
            });
            console.log(val.context.label);
          });
        }
      });
  }

  useEffect(() => {
    console.log("MESSAGE ERROR ", messageError.industry);
  }, [isError]);

  function selectIndustry(e) {
    setselectedIndustry(e);
    if (e.value == "other") {
      setisOther(true);
      setform({ ...form, ["industry"]: null });
    } else if (e.value == 0) {
      setisOther(false);
      setform({ ...form, ["industry"]: null });
    } else {
      setisOther(false);
      setform({ ...form, ["industry"]: e.value });
      setisError({ ...isError, ["industry"]: false });
      setmessageError({ ...messageError, ["industry"]: null });
    }
  }

  function clearFormIndustry() {
    setform({ ...form, ["industry"]: "" });
    setisOther(false);
    setselectedIndustry(dataIndustry[0]);
  }

  const formProps = {
    form,
    onChange,
    phoneChange,
    submit,
    isError,
    messageError,
    isOther,
    selectedIndustry,
    selectIndustry,
    clearFormIndustry,
    loading,
    recaptchaToken,
    setRecaptchaToken,
    recaptchaRef,
  };

  return (
    <>
      <Head>
        <title>Contact Us | AI Bookkeeping Australia - Ledgerowl</title>
        <meta
          property="og:description"
          content="Get in touch with Ledgerowl. Book a demo of AI-powered bookkeeping with expert accountant oversight for your Australian business."
        />
        <meta property="og:title" content="Contact Us | AI Bookkeeping Australia - Ledgerowl" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/contact`} />
        <meta property="og:image" content="https://ledgerowl.com.au/favicon.png" />
        <meta property="og:site_name" content="Ledgerowl" />
        <meta property="og:locale" content="en_AU" />
        <meta name="title" content="Contact Us | AI Bookkeeping Australia - Ledgerowl" />
        <meta
          name="description"
          content="Get in touch with Ledgerowl. Book a demo of AI-powered bookkeeping with expert accountant oversight for your Australian business."
        />
        <meta
          name="keywords"
          content="AI bookkeeping Australia, AI accounting service Australia, contact Ledgerowl, book a demo, BAS agent Sydney, small business bookkeeper Sydney, human verified bookkeeping"
        />
        <link rel="canonical" href={`${SITE_URL}/contact`} />
      </Head>

      {/* ═══ DESKTOP LAYOUT ═══ */}
      <div className="w-full lg:flex hidden h-screen bg-[#EBF5FF] p-6 gap-6">
        {/* Left Panel — Illustration Hero */}
        <div className="w-1/2 h-full">
          <ContactHeroPanel />
        </div>

        {/* Right Panel — Contact Form in White Card */}
        <div className="w-1/2 h-full flex items-center justify-center">
          {success ? (
            <SuccessContactUs country={query?.country} />
          ) : (
            <div className="flex flex-col bg-white rounded-2xl shadow-[0px_8px_16px_0px_rgba(55,65,81,0.08)] px-10 py-8 w-[520px]">
              {/* Back */}
              <div className="flex items-center gap-2 mb-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-2 text-[#333] hover:text-gray-cool-900 transition-colors text-[14px] font-medium"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
              </div>

              {/* Header with owl logo */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-inter text-[24px] font-semibold text-[#333]">
                  Contact Us
                </h2>
                <Link href="/" className="flex hover:cursor-pointer" aria-label="Home">
                  <Image
                    src="/favicon.png"
                    alt="Ledgerowl"
                    width={32}
                    height={32}
                    className="h-8 w-8"
                    unoptimized
                  />
                </Link>
              </div>

              {/* Form */}
              <ContactForm {...formProps} />
            </div>
          )}
        </div>
      </div>

      {/* ═══ MOBILE LAYOUT ═══ */}
      <div className="lg:hidden flex flex-col w-full min-h-screen bg-white">
        {/* Mobile Hero */}
        <div className="p-4">
          <div className="bg-gradient-to-b from-[#E8F0FE] via-[#E4EDFC] to-[#D6E4FA] rounded-2xl p-6 relative overflow-hidden">
            <div className="flex items-center justify-between mb-6">
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-1.5 text-gray-cool-600 hover:text-gray-cool-900 transition-colors text-[14px] font-medium"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <Link href="/">
                <Logo className="h-5 w-auto" />
              </Link>
            </div>
            <h1 className="font-inter text-[28px] font-bold text-blue-primary-900 leading-[1.15] tracking-[-0.5px]">
              {"Let's"} work together
            </h1>
            <p className="mt-2 font-inter text-[14px] text-gray-cool-500 leading-[1.6]">
              {"We'd"} love to hear from you! Send us a message using the form
              below, or email us.
            </p>

            {/* Decorative dots on mobile */}
            <div className="absolute top-4 right-16 w-2 h-2 rounded-full bg-blue-primary-200/50" />
            <div className="absolute bottom-6 right-8 w-3 h-3 rounded-full bg-blue-primary-200/50" />
            <div className="absolute bottom-10 left-[40%] w-2 h-2 rounded-full bg-blue-primary-200/50" />
          </div>
        </div>

        {/* Mobile Form */}
        <div className="flex flex-col px-5 pb-10">
          <div className="mt-6 flex w-full flex-col">
            {success ? (
              <SuccessContactUs country={query?.country} />
            ) : (
              <div className="flex flex-col w-full md:px-10 justify-center items-center">
                <div className="flex w-full flex-col">
                  <div className="flex items-center justify-between mb-5">
                    <p className="font-inter text-[20px] font-bold text-gray-cool-900">
                      Contact Us
                    </p>
                    <Image
                      src="/favicon.png"
                      alt="Ledgerowl"
                      width={32}
                      height={32}
                      className="h-8 w-8"
                      unoptimized
                    />
                  </div>
                  <ContactForm {...formProps} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
